import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { isMenuItemActive, useMenuItems } from '@/entities/sidebar';
import { useLogout } from '@/features/auth';
import { Icon } from '@/shared/IconCollecion';

// Рейка планшета (md, 768–1023px) для compactNav = 'rail-topbar', макет telecom
// (раздел 4): полоса 84px, иконка и мелкая подпись под ней, «Выйти» внизу.
// Логотипа в рейке нет — по макету она начинается сразу с «Дашборда».
//
// Группа меню здесь — одна кнопка: подпункты в 84px не помещаются. Она ведёт на
// основной экран группы и подсвечена на любом из её подпунктов, а переключение
// между ними — табами-сегментами на странице (NavGroup.compactTabs).

const itemClassName = (active: boolean) =>
  cn(
    'relative flex flex-col items-center gap-[5px] rounded-surface-sm px-1 py-2.5 text-center text-[10px] font-semibold leading-3 transition-colors',
    active
      ? 'bg-action-primary text-content-inverse'
      : 'text-content-inverse-muted hover:bg-surface-sidebar-hover hover:text-content-inverse',
  );

export const SidebarRail = () => {
  const items = useMenuItems();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const { t } = useTranslation('common');

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Высота — ровно окно, а не «не меньше окна»: с min-h рейка (~585px) на
  // телефоне в ландшафте шире 768px (iPhone 14 — 844×390) уходила ниже края, и
  // «Настройки», «Тикеты», «Выйти» были видны только в самом конце страницы.
  // Теперь то, что не влезло, прокручивается внутри рейки.
  return (
    <aside className="sticky top-0 hidden h-dvh w-[84px] min-w-[84px] flex-col gap-1 self-start overflow-y-auto overscroll-contain bg-surface-sidebar px-2 py-5 md:flex lg:hidden">
      <nav className="flex flex-col gap-1">
        {items.map((item) => {
          const active = isMenuItemActive(item, pathname);

          return (
            <Link key={item.link} to={item.link} aria-current={active ? 'page' : undefined} className={itemClassName(active)}>
              <Icon name={item.iconName} color="currentColor" className="size-5" />
              {item.label}
              {Boolean(item.badge) && (
                <span
                  className={cn(
                    'absolute right-3.5 top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[5px] text-[10px] font-bold',
                    active ? 'bg-content-inverse text-content-accent' : 'bg-action-primary text-content-inverse',
                  )}
                >
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      <button type="button" onClick={handleLogout} className={cn(itemClassName(false), 'mt-auto')}>
        <Icon name="Log_Out" color="currentColor" className="size-5" />
        {t('logout')}
      </button>
    </aside>
  );
};

export default SidebarRail;
